import { useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';

/**
 * Comprehensive device detection utility
 * Handles iPhone, Android, tablets, and desktop devices
 */
export const useDeviceDetection = () => {
  // Mobile devices (phones) - covers iPhone 12/13/14, Android phones
  const isMobilePhone = useMediaQuery('(max-width: 480px)');

  // Tablet devices - covers iPad, Android tablets
  const isTablet = useMediaQuery('(min-width: 481px) and (max-width: 1024px)');

  // All mobile devices (phones + tablets) - anything that should use mobile layout
  const isMobileDevice = useMediaQuery('(max-width: 1024px)');

  // Desktop/laptop devices
  const isDesktop = useMediaQuery('(min-width: 1025px)');

  // Specific device type detection
  const deviceType = isMobilePhone ? 'phone' : isTablet ? 'tablet' : isDesktop ? 'desktop' : 'unknown';

  return {
    isMobilePhone,
    isTablet,
    isMobileDevice,
    isDesktop,
    deviceType
  };
};

/**
 * Stable mobile detection hook that prevents layout switching
 * Uses window dimensions and user agent for more reliable detection
 * Now properly handles tablet orientation for landscape vs portrait layouts
 */
export const useStableMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const userAgent = navigator.userAgent.toLowerCase();

      // Check for mobile devices (phones) - strict width check for phones
      const isPhone = width <= 768;

      // Check for tablets - devices that are wider than phones but not desktop
      const isTablet = width > 768 && width <= 1366;

      // For tablets, check orientation:
      // - Landscape: width > height (should use desktop layout)
      // - Portrait: height > width (should use mobile layout)
      const isTabletPortrait = isTablet && height > width;

      // Mobile devices are phones OR tablets in portrait mode
      const isMobileDevice = isPhone || isTabletPortrait;

      setIsMobile(isMobileDevice);
      setIsInitialized(true);

      // Debug logging (remove in production)
      if (import.meta.env.DEV) {
        console.log('🔍 Stable Mobile Detection:', {
          width,
          height,
          isPhone,
          isTablet,
          isTabletPortrait,
          isMobileDevice,
          orientation: width > height ? 'landscape' : 'portrait',
          userAgent: userAgent.substring(0, 50) + '...'
        });
      }
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return { isMobile, isInitialized };
};

/**
 * Check if device should use mobile layout
 * This includes phones and tablets but excludes desktop
 * Now properly handles tablet orientation
 */
export const shouldUseMobileLayout = () => {
  if (typeof window === 'undefined') return false;

  const width = window.innerWidth;
  const height = window.innerHeight;

  // Check for mobile devices (phones) - strict width check for phones
  const isPhone = width <= 768;

  // Check for tablets - devices that are wider than phones but not desktop
  const isTablet = width > 768 && width <= 1366;

  // For tablets, check orientation:
  // - Landscape: width > height (should use desktop layout)
  // - Portrait: height > width (should use mobile layout)
  const isTabletPortrait = isTablet && height > width;

  // Mobile devices are phones OR tablets in portrait mode
  return isPhone || isTabletPortrait;
};

/**
 * More specific mobile detection for phones only
 * Covers iPhone 12/13/14 and most Android phones
 */
export const isMobilePhone = () => {
  return useMediaQuery('(max-width: 480px)');
};

/**
 * Check if device is a phone specifically
 */
export const isPhoneDevice = () => {
  return useMediaQuery('(max-width: 480px)');
};

/**
 * Check if device is a tablet specifically
 */
export const isTabletDevice = () => {
  return useMediaQuery('(min-width: 481px) and (max-width: 1024px)');
};
